import { reactive } from "vue";
import { Facade } from "@/drawing";
import type { DwgBlockInfo } from "@/drawing/blocks";
import type { Legend } from "@/types/legend.types";
import { legendDwgCacheRepository } from "@/repositories/legendDwgCache.repository";

type TemplateBlocksState = {
  blocks: DwgBlockInfo[];
  isLoaded: boolean;
  isLegendSource: boolean; // true when blocks come from the active legend's DWG
  load: (arrayBuffer: ArrayBuffer) => void;
  loadFromLegend: (legend: Legend) => Promise<void>;
  clear: () => void;
  importBlock: (blockName: string) => boolean;
};

export default reactive<TemplateBlocksState>({
  blocks: [],
  isLoaded: false,
  isLegendSource: false,

  load(arrayBuffer: ArrayBuffer) {
    if (!Facade.isInitialized) return;
    const blocks = Facade.blocks.loadTemplateFromBuffer(arrayBuffer);
    this.blocks = blocks;
    this.isLoaded = blocks.length > 0;
    this.isLegendSource = false;
  },

  async loadFromLegend(legend: Legend) {
    const dwgInfo = legend.dwg_file_info;
    if (!dwgInfo) {
      // Légende sans DWG : vider l'état si la source était la légende
      if (this.isLegendSource) this.clear();
      return;
    }

    try {
      let buffer = await legendDwgCacheRepository.get(legend.id, dwgInfo.id);

      if (!buffer) {
        const response = await fetch(dwgInfo.download_url);
        if (!response.ok) throw new Error("DWG download failed");
        buffer = await response.arrayBuffer();
        await legendDwgCacheRepository.put(legend.id, dwgInfo.id, buffer);
      }

      if (!Facade.isInitialized) return;
      const blocks = Facade.blocks.loadTemplateFromBuffer(buffer);
      this.blocks = blocks;
      this.isLoaded = blocks.length > 0;
      this.isLegendSource = true;
    } catch (err) {
      console.warn("[TemplateBlocksState] Failed to load legend DWG", err);
    }
  },

  clear() {
    if (Facade.isInitialized) {
      Facade.blocks.clearTemplate();
    }
    this.blocks = [];
    this.isLoaded = false;
    this.isLegendSource = false;
  },

  importBlock(blockName: string): boolean {
    if (!Facade.isInitialized) return false;
    return Facade.blocks.importTemplateBlock(blockName);
  },
});
